// Psychographic Profiling — 5 dimensions with polar labels
// Each dimension is a spectrum from 0 (left label) to 100 (right label)

export const PSYCHOGRAPHIC_DIMENSIONS = [
  { key: 'planning', labelLow: 'Spontaneous', labelHigh: 'Planner', icon: '🗺️' },
  { key: 'social', labelLow: 'Solo Explorer', labelHigh: 'Social Butterfly', icon: '👥' },
  { key: 'pace', labelLow: 'Slow & Deep', labelHigh: 'Fast & Wide', icon: '⚡' },
  { key: 'comfort', labelLow: 'Roughing It', labelHigh: 'Luxury Seeker', icon: '✨' },
  { key: 'culture', labelLow: 'Tourist Trail', labelHigh: 'Off the Beaten Path', icon: '🧭' },
];

// Travel Soul archetypes — derived from psychographic profile
export const TRAVEL_SOUL_LABELS = {
  wanderer: { label: 'The Wanderer', description: 'You follow your instincts, embracing the unknown with open arms.' },
  curator: { label: 'The Curator', description: 'Every trip is a carefully crafted collection of experiences.' },
  connector: { label: 'The Connector', description: 'Travel is about the people you meet and stories you share.' },
  seeker: { label: 'The Seeker', description: 'You chase meaning, depth, and transformation in every journey.' },
  epicurean: { label: 'The Epicurean', description: 'Life is short — you travel for the finest experiences.' },
};

// 5 scenario-based soul quiz questions — each choice shifts 2 dimensions
export const SOUL_QUIZ_QUESTIONS = [
  {
    id: 'q1',
    scenario: 'You arrive in a new city with a free afternoon. You…',
    choiceA: { text: 'Wander with no plan and see what happens', signals: { planning: -15, culture: 10 } },
    choiceB: { text: 'Check your saved list of must-see spots', signals: { planning: 15, culture: -5 } },
  },
  {
    id: 'q2',
    scenario: 'Your ideal travel companion is…',
    choiceA: { text: 'Just me — I set my own pace', signals: { social: -15, pace: -5 } },
    choiceB: { text: 'A group of friends or fellow travelers', signals: { social: 15, pace: 5 } },
  },
  {
    id: 'q3',
    scenario: 'You have one day left on your trip. You…',
    choiceA: { text: 'Revisit a favorite spot and soak it in', signals: { pace: -15, comfort: 5 } },
    choiceB: { text: 'Squeeze in three more things from the list', signals: { pace: 15, comfort: -5 } },
  },
  {
    id: 'q4',
    scenario: 'Your dream accommodation is…',
    choiceA: { text: 'A cozy hostel or homestay with local character', signals: { comfort: -15, culture: 10 } },
    choiceB: { text: 'A boutique hotel with amazing amenities', signals: { comfort: 15, culture: -5 } },
  },
  {
    id: 'q5',
    scenario: 'When choosing what to eat abroad, you…',
    choiceA: { text: 'Follow the locals to a hidden street stall', signals: { culture: 15, comfort: -10 } },
    choiceB: { text: 'Book a well-reviewed restaurant in advance', signals: { culture: -10, planning: 10 } },
  },
];

/**
 * Compute psychographic profile from quiz answers.
 * @param {Object} answers — { q1: 'A'|'B', q2: 'A'|'B', ... }
 * @returns {{ planning: number, social: number, pace: number, comfort: number, culture: number }}
 */
export function computePsychographicProfile(answers) {
  const profile = { planning: 50, social: 50, pace: 50, comfort: 50, culture: 50 };

  SOUL_QUIZ_QUESTIONS.forEach((q) => {
    const pick = answers[q.id];
    if (!pick) return;
    const signals = pick === 'A' ? q.choiceA.signals : q.choiceB.signals;
    Object.entries(signals).forEach(([dim, delta]) => {
      profile[dim] = Math.max(0, Math.min(100, profile[dim] + delta));
    });
  });

  return profile;
}

/**
 * Derive a Travel Soul archetype from the psychographic profile.
 * @param {{ planning: number, social: number, pace: number, comfort: number, culture: number }} profile
 * @returns {string} — key into TRAVEL_SOUL_LABELS
 */
export function deriveTravelSoul(profile) {
  // Scoring heuristic: each archetype favors certain dimension ranges
  const scores = {
    wanderer: (100 - profile.planning) * 0.4 + profile.culture * 0.3 + (100 - profile.comfort) * 0.3,
    curator: profile.planning * 0.4 + (100 - profile.pace) * 0.3 + profile.comfort * 0.3,
    connector: profile.social * 0.5 + profile.pace * 0.2 + (100 - profile.culture) * 0.3,
    seeker: profile.culture * 0.4 + (100 - profile.pace) * 0.3 + (100 - profile.comfort) * 0.3,
    epicurean: profile.comfort * 0.4 + profile.planning * 0.2 + (100 - profile.culture) * 0.2 + profile.social * 0.2,
  };

  return Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
}
